// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { forwardRef, useContext, useImperativeHandle, useRef, useState } from 'react';
import clsx from 'clsx';
import { StickyHeaderContext } from '../container/use-sticky-header';
import { useStickyHeaderCopy } from '../internal/components/sticky-header/use-sticky-header-copy';
import { getVisualContextClassname } from '../internal/components/visual-context';
import { TableColGroup } from './column-groups/col-group';
import { getTableRoleProps } from './table-role';
import Thead from './thead';
import styles from './styles.css.js';
export default forwardRef(StickyHeader);
function StickyHeader({ variant, theadProps, wrapperRef, theadRef, secondaryWrapperRef, tableRef, onScroll, tableHasHeader, contentDensity, tableRole, hasGroupedColumns, columnDefinitions, hasSelection, }, ref) {
    const secondaryTheadRef = useRef(null);
    const secondaryTableRef = useRef(null);
    const { isStuck } = useContext(StickyHeaderContext);
    const [focusedComponent, setFocusedComponent] = useState(null);
    const { scrollToRow, scrollToTop } = useStickyHeaderCopy({
        tableRef,
        theadRef,
        secondaryTheadRef,
        secondaryTableRef,
        tableWrapperRef: wrapperRef,
    });
    useImperativeHandle(ref, () => ({
        scrollToTop,
        scrollToRow,
        setFocus: setFocusedComponent,
    }));
    // For grouped columns, the secondary table needs a <colgroup> to define column
    // widths. Without it, table-layout:fixed uses the first row (which has colspan group
    // headers) to determine widths — giving wrong results.
    return (React.createElement("div", { className: clsx(styles['header-secondary'], styles[`variant-${variant}`], {
            [styles['table-has-header']]: tableHasHeader,
        }), "aria-hidden": true, 
        // Prevents receiving focus in Firefox. Focus on the overflowing table is sufficient
        // to scroll the table horizontally
        tabIndex: -1, ref: secondaryWrapperRef, onScroll: onScroll },
        React.createElement("table", { className: clsx(styles.table, styles['table-layout-fixed'], hasGroupedColumns && styles['has-grouped-header'], contentDensity === 'compact' && getVisualContextClassname('compact-table')), ref: secondaryTableRef, ...getTableRoleProps({ tableRole }) },
            hasGroupedColumns && columnDefinitions && (React.createElement(TableColGroup, { visibleColumnDefinitions: columnDefinitions, hasSelection: !!hasSelection, sticky: true, selectionColumnId: theadProps.selectionColumnId })),
            React.createElement(Thead, { ref: secondaryTheadRef, sticky: true, stuck: isStuck, focusedComponent: focusedComponent, ...theadProps }))));
}
//# sourceMappingURL=sticky-header.js.map